import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, X, AlertTriangle, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const UpgradeBanner = ({ 
  programsUsed, 
  programsLimit, 
  athletesUsed, 
  athletesLimit,
  isAtProgramLimit,
  isAtAthleteLimit,
  onDismiss,
  variant = 'approaching' // 'approaching' or 'limit_reached'
}) => {
  const programsPercent = programsLimit > 0 ? Math.min((programsUsed / programsLimit) * 100, 100) : 0;
  const athletesPercent = athletesLimit > 0 ? Math.min((athletesUsed / athletesLimit) * 100, 100) : 0;

  const isLimitReached = isAtProgramLimit || isAtAthleteLimit;

  if (isLimitReached) {
    return (
      <div 
        className="relative bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg p-4 mb-6 shadow-lg"
        data-testid="upgrade-banner-limit-reached"
      >
        {onDismiss && (
          <button 
            onClick={onDismiss} 
            className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded transition-colors"
            data-testid="dismiss-upgrade-banner"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">You've reached your Free plan limit!</h3>
              <p className="text-white/90 text-sm mt-1">
                {isAtProgramLimit && isAtAthleteLimit ? (
                  "You've hit the maximum for both programs and athletes."
                ) : isAtProgramLimit ? (
                  `You've reached the ${programsLimit} program limit.`
                ) : (
                  `You've reached the ${athletesLimit} athlete limit.`
                )}
                {' '}Upgrade to Pro for unlimited access.
              </p>
            </div>
          </div>
          
          <Link to="/subscription">
            <Button 
              className="bg-white text-orange-600 hover:bg-white/90 font-semibold shadow-md"
              data-testid="upgrade-now-button"
            >
              <Zap className="h-4 w-4 mr-2" />
              Upgrade Now
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Approaching limit variant
  return (
    <div 
      className="relative bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg p-4 mb-6 shadow-lg"
      data-testid="upgrade-banner-approaching"
    >
      {onDismiss && (
        <button 
          onClick={onDismiss} 
          className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded transition-colors"
          data-testid="dismiss-upgrade-banner"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start space-x-3 flex-1">
          <div className="p-2 bg-white/20 rounded-lg shrink-0">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg">You're growing fast!</h3>
            <p className="text-white/90 text-sm mt-1">
              You're approaching your Free plan limits. Upgrade to Pro for unlimited programs, athletes, and more!
            </p>
            
            {/* Usage Progress Bars */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Programs</span>
                  <span className="font-semibold">{programsUsed}/{programsLimit}</span>
                </div>
                <Progress 
                  value={programsPercent} 
                  className="h-2 bg-white/30"
                />
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Athletes</span>
                  <span className="font-semibold">{athletesUsed}/{athletesLimit}</span>
                </div>
                <Progress 
                  value={athletesPercent} 
                  className="h-2 bg-white/30"
                />
              </div>
            </div>
          </div>
        </div>
        
        <Link to="/subscription" className="shrink-0">
          <Button 
            className="bg-white text-indigo-600 hover:bg-white/90 font-semibold shadow-md w-full lg:w-auto"
            data-testid="upgrade-to-pro-button"
          >
            <Zap className="h-4 w-4 mr-2" />
            Upgrade to Pro
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default UpgradeBanner;
